import { type Session } from '@supabase/supabase-js';
import { supabase } from '@shared/lib/supabase';
import { type AuthRepository } from '../application/auth.repository';
import { type AuthSession } from '../domain/session';
import { type Credentials, type SignUpInput } from '../domain/credentials';

function toAuthSession(session: Session | null): AuthSession | null {
  if (!session) return null;
  return {
    user: {
      id: session.user.id,
      email: session.user.email ?? null,
    },
    expiresAt: session.expires_at ?? null,
  };
}

function authError(message: string, cause?: unknown): Error {
  return new Error(message, cause === undefined ? undefined : { cause });
}

export const supabaseAuthRepository: AuthRepository = {
  async signUp({ email, password, displayName }: SignUpInput): Promise<AuthSession> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
      },
    });
    if (error) throw authError(error.message, error);
    const session = toAuthSession(data.session);
    if (!session) {
      // Email confirmation is enabled — user exists but session is null until confirmed.
      throw authError('Подтверди регистрацию по ссылке из письма, потом войди ещё раз.');
    }
    return session;
  },

  async signIn({ email, password }: Credentials): Promise<AuthSession> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw authError(error.message, error);
    const session = toAuthSession(data.session);
    if (!session) throw authError('Не удалось получить сессию');
    return session;
  },

  async signInWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/`,
        queryParams: { prompt: 'select_account' },
      },
    });
    if (error) throw authError(error.message, error);
  },

  async requestPasswordReset(email: string): Promise<void> {
    // Supabase will send a magic recovery link if the email maps to a user.
    // The link drops the user back on the app root with a recovery session;
    // we will surface the "set new password" step in a follow-up — for now
    // the user can just sign in with the temp session and update via Profile
    // once that flow lands.
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });
    if (error) throw authError(error.message, error);
  },

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw authError(error.message, error);
  },

  async getCurrentSession(): Promise<AuthSession | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw authError(error.message, error);
    return toAuthSession(data.session);
  },

  onSessionChange(listener) {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      listener(toAuthSession(session));
    });
    return () => {
      data.subscription.unsubscribe();
    };
  },
};
